import { Button, StackProps } from '@mui/material';
import { ButtonWrapper } from '@/components/@IntranetLibrary/Button/ButtonWrapper';
import React, { useRef, useState } from 'react';
import {
  CreateBoardStep,
  CreateBoardStepValues,
} from '@/components/Board/CreateBoardModal/index';
import ArrowIcon from '@/public/icons/arrow.svg';
import classNames from 'classnames';
import { CreateBoardFooterWrapper } from '@/components/Board/CreateBoardModal/styles';
import { useTranslation } from 'react-i18next';

type CreateBoardFooterProps = {
  step: CreateBoardStep;
  setStep: (step: CreateBoardStep) => void | Promise<void>;
  isAvailableSave: boolean;
  disabledSubmit: boolean;
  disabledSave: boolean;
  formType: 'editor' | 'codeUpload';
  isDeploying: boolean;
  handlePreviewForCodeUpload: () => Promise<void>;
  handleSave: () => void | Promise<void>;
  handleSubmit: () => void;
  description?: string;
} & Omit<StackProps, 'direction'>;

const CreateBoardFooter = (_props: CreateBoardFooterProps) => {
  const {
    step,
    setStep,
    isAvailableSave,
    disabledSubmit,
    disabledSave,
    formType,
    isDeploying,
    handlePreviewForCodeUpload,
    handleSave,
    handleSubmit,
    description,
    ...props
  } = _props;
  const { t } = useTranslation();
  const [isPreviewing, setIsPreviewing] = useState(false);
  const isPreviewingRef = useRef(false);

  const handlePreviewClick = async () => {
    if (isPreviewingRef.current) return;

    isPreviewingRef.current = true;
    setIsPreviewing(true);

    try {
      if (formType === 'codeUpload') {
        // 코드 업로드: 배포 프로세스 시작 (내부에서 저장 처리)
        await handlePreviewForCodeUpload();
      } else {
        // 일반 에디터: 바로 미리보기로 step 변경
        await setStep(CreateBoardStepValues.Preview);
      }
    } finally {
      isPreviewingRef.current = false;
      setIsPreviewing(false);
    }
  };

  return (
    <CreateBoardFooterWrapper className={classNames(`step-${step}`)} {...props}>
      {description && <p>{description}</p>}
      <ButtonWrapper>
        {isAvailableSave && (
          <Button
            variant={'outlined'}
            size={'large'}
            disabled={disabledSave || isDeploying}
            onClick={handleSave}
            className={'save'}
            sx={{ width: '140px' }}
          >
            {t('임시저장')}
          </Button>
        )}

        <Button
          variant={'contained'}
          size={'large'}
          disabled={disabledSubmit || isDeploying || isPreviewing}
          onClick={handlePreviewClick}
          className={'preview'}
          sx={{ width: '200px' }}
        >
          미리보고 등록하기
        </Button>

        <Button
          id={'create-board-button-back'}
          variant={'outlined'}
          size={'large'}
          onClick={() => setStep(CreateBoardStepValues.WriteForm)}
          startIcon={<ArrowIcon width={'20px'} height={'20px'} />}
          className={'back'}
          sx={{ minWidth: '171px' }}
        >
          작성으로 돌아가기
        </Button>

        <Button
          id={'create-board-button-submit'}
          variant={'contained'}
          size={'large'}
          disabled={disabledSubmit}
          onClick={handleSubmit}
          className={'submit'}
          sx={{ minWidth: '200px' }}
        >
          등록하기
        </Button>
      </ButtonWrapper>
    </CreateBoardFooterWrapper>
  );
};

export default CreateBoardFooter;
